import { _ as _toConsumableArray } from '../../unsorted/toConsumableArray.js';
import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { ClassNames } from '@emotion/react';
import { AnimatePresence } from 'framer-motion';
import { isValidElement, useMemo, useCallback } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { DEFAULT_CHIP_SIZE, CHIP_SIZES } from '../../constants.js';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { A as Avatar } from '../Avatar/Avatar.js';
import { getResponsiveSx, getStartingSize } from '../../utils/styleHelpers.js';
import { hFlex } from '../../utils/sxChunks.js';
import { F as FramedImage } from '../FramedImage/FramedImage.js';
import { useTheme } from '../../hooks/useTheme.js';
import { useGetCurrentSizeClass } from '../../hooks/useGetCurrentSizeClass.js';
import { i as isImageResizerProps } from '../../utils/imageHelpers2.js';
import { I as Icon } from '../Icon/Icon.js';
import { useSplitApartChildrenAndSubComponents } from '../../hooks/useSplitApartChildrenAndSubComponents.js';
import { S as SmartClone } from '../SmartClone/SmartClone.js';
import { C as ClearFieldButton } from '../ClearFieldButton/ClearFieldButton.js';
import { _ as _typeof } from '../../unsorted/typeof.js';
import { B as Box } from '../Box/Box.js';
import { useGetSubcomponentChild } from '../../hooks/useGetSubcompnentChild.js';
import { B as Body } from '../Body/Body.js';
import { B as BaseClickable } from '../BaseClickable/BaseClickable.js';

// type moo = GetHybridClickableRCandDomProps<undefined, Function>['domRef'];

function isPotentialComponentWithClickableProps(rc) {
  return _typeof(rc) === "object";
}
function isClickableRc(rc) {
  var isComponent = /*#__PURE__*/isValidElement(rc);
  // @NOTE: early exit if its not a component:
  if (!isComponent) return false;

  // @NOTE: try to detect Link components from next/link & react-router-dom etc
  // (a previous iteration of this logic attemtped to try do `/Link/.match(rc.type.render.name)`
  // but the name of the component function can be minified in production builds 🤦‍♂️🤪)
  if (isPotentialComponentWithClickableProps(rc)) {
    var _rc$props, _rc$props2, _rc$props3;
    return Boolean(rc === null || rc === void 0 || (_rc$props = rc.props) === null || _rc$props === void 0 ? void 0 : _rc$props.onClick) || Boolean(rc === null || rc === void 0 || (_rc$props2 = rc.props) === null || _rc$props2 === void 0 ? void 0 : _rc$props2.href) || Boolean(rc === null || rc === void 0 || (_rc$props3 = rc.props) === null || _rc$props3 === void 0 ? void 0 : _rc$props3.to);
  }

  // @NOTE: if its none of the above, then its not clickable
  return false;
}
function isBaseClickable(onClick, rc, containsClickables, props) {
  return isClickableRc(rc) || !containsClickables && Boolean(onClick);
}
function isBox(onClick, rc, containsClickables, props) {
  return containsClickables || !isClickableRc(rc) && !onClick;
}

var _excluded$3 = ["size", "avatarSize", "className", "rc", "use"];
function ownKeys$4(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$4(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$4(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$4(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ChipAvatar(_ref) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_CHIP_SIZE : _ref$size,
    avatarSize = _ref.avatarSize,
    className = _ref.className,
    _ref$rc = _ref.rc,
    rc = _ref$rc === void 0 ? jsx("span", {}) : _ref$rc,
    use = _ref.use,
    props = _objectWithoutProperties(_ref, _excluded$3);
  var _ref2 = "imageUrl" in props ? props : _objectSpread$4(_objectSpread$4({}, props), {}, {
      imageUrl: undefined
    }),
    imageUrl = _ref2.imageUrl;
  var pickedAvatarSize = useMemo(function () {
    if (Array.isArray(size)) {
      return size.map(function (s) {
        return s === "large" ? "medium" : s === "medium" ? "small" : null;
      });
    }
    return size === "large" ? "medium" : "small";
  }, [size]);
  return jsx(Avatar, _objectSpread$4(_objectSpread$4({}, props), {}, {
    use: use,
    imageUrl: imageUrl,
    rc: rc,
    className: "".concat(className !== null && className !== void 0 ? className : "", " ChipAvatar"),
    size: avatarSize !== null && avatarSize !== void 0 ? avatarSize : pickedAvatarSize
  }));
}
ChipAvatar.displayName = "Chip.Avatar";

function ownKeys$3(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$3(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$3(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$3(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var baseChipStyles = _objectSpread$3(_objectSpread$3({}, hFlex), {}, {
  alignItems: "center",
  gap: "base.spacing.x2",
  px: "base.spacing.x3",
  bg: "base.color.translucent.emphasis.100",
  brad: "base.borderRadius.x4",
  transitionDuration: "base.motion.normal.fast.cssDuration",
  transitionProperty: "background, border-color",
  transitionTimingFunction: "base.motion.normal.fast.cssEase",
  "&.Chip--inverseVariant": {
    bg: "base.color.translucent.inverse.200",
    "& .Chip__label": {
      c: "base.color.text.body.inverse.primary"
    },
    "& .ChipIcon": {
      fill: "base.color.text.body.inverse.primary"
    },
    "& .ClearFieldButton": {
      fill: "base.color.translucent.inverse.600",
      "&:hover": {
        fill: "base.color.text.body.inverse.primary"
      }
    }
  },
  "&.Chip--mediumSize": {
    py: "base.spacing.x1",
    minh: "base.icon.size.400"
  },
  "&.Chip--largeSize": {
    py: "base.spacing.x2",
    minh: "base.icon.size.500"
  },
  "&.Chip--hasWholeOnClick": {
    cursor: "pointer",
    border: "solid",
    borderWidth: "base.border.size.100",
    borderColor: "base.color.translucent.standard.500",
    "&:hover": {
      bg: "base.color.translucent.emphasis.200",
      borderColor: "base.color.translucent.standard.800"
    },
    "&:active": {
      bg: "base.color.translucent.emphasis.300",
      borderColor: "base.color.translucent.standard.800"
    }
  }
});
var labelSx = {
  flex: 1
};
var baseChipLeftImageContentSx = {
  flexShrink: 0,
  h: "auto"
};
function getChipIconSx(_ref) {
  var size = _ref.size,
    base = _ref.theme.base;
  return size === "large" ? {
    width: base.icon.size[200]
  } : {
    width: base.icon.size[100]
  };
}
var getResponsiveChipIconSx = function getResponsiveChipIconSx(props) {
  return getResponsiveSx(_objectSpread$3(_objectSpread$3({}, props), {}, {
    renderSxAtSize: getChipIconSx
  }));
};
function getChipFramedImageSx(_ref2) {
  var size = _ref2.size,
    base = _ref2.theme.base;
  return size === "large" ? {
    width: base.icon.size[400]
  } : {
    width: base.icon.size[300]
  };
}
var getResponsiveChipFramedImageSx = function getResponsiveChipFramedImageSx(props) {
  return getResponsiveSx(_objectSpread$3(_objectSpread$3({}, props), {}, {
    renderSxAtSize: getChipFramedImageSx
  }));
};

var _excluded$2 = ["size", "className", "sx", "use"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ChipFramedImage(_ref) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_CHIP_SIZE : _ref$size,
    className = _ref.className,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    use = _ref.use,
    props = _objectWithoutProperties(_ref, _excluded$2);
  var theme = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_CHIP_SIZE, CHIP_SIZES);
  var mergedSx = useMemo(function () {
    return merge(baseChipLeftImageContentSx, {
      w: startingSize === "large" ? "base.icon.size.400" : "base.icon.size.300"
    }, getResponsiveChipFramedImageSx({
      size: size,
      theme: theme
    }), sx);
  }, [sx, size, startingSize, theme]);
  var shouldUseImageResizer = isImageResizerProps(use);
  var currentSizeClass = useGetCurrentSizeClass(size, DEFAULT_CHIP_SIZE, CHIP_SIZES);
  var typedProps = useMemo(function () {
    return shouldUseImageResizer ? _objectSpread$2(_objectSpread$2({}, props), {}, {
      relativeImageSizeInLayout: currentSizeClass === "large" ? theme.base.icon.size[400] : theme.base.icon.size[300]
    }) : _objectSpread$2(_objectSpread$2({}, props), {}, {
      use: use
    });
  }, [use, props, currentSizeClass, shouldUseImageResizer, theme.base.icon.size]);
  return jsx(FramedImage, _objectSpread$2(_objectSpread$2({}, typedProps), {}, {
    className: "".concat(className !== null && className !== void 0 ? className : "", " ChipFramedImage ChipFramedImage--").concat(currentSizeClass),
    sx: mergedSx
  }));
}
ChipFramedImage.displayName = "Chip.FramedImage";

var _excluded$1 = ["size", "className", "sx"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ChipIcon(_ref) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_CHIP_SIZE : _ref$size,
    className = _ref.className,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var theme = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_CHIP_SIZE, CHIP_SIZES);
  var mergedSx = useMemo(function () {
    return merge(baseChipLeftImageContentSx, {
      fill: "base.color.text.body.primary",
      w: startingSize === "large" ? "base.icon.size.200" : "base.icon.size.100"
    }, getResponsiveChipIconSx({
      size: size,
      theme: theme
    }), sx);
  }, [size, startingSize, theme, sx]);
  return jsx(Icon, _objectSpread$1(_objectSpread$1({}, props), {}, {
    className: "".concat(className !== null && className !== void 0 ? className : "", " ChipIcon"),
    sx: mergedSx
  }));
}
ChipIcon.displayName = "Chip.Icon";

var _excluded = ["label", "className", "rc", "children", "testId", "sx", "size", "variant", "onRemove"],
  _excluded2 = ["onClick"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function Chip(_ref) {
  var label = _ref.label,
    className = _ref.className,
    rc = _ref.rc,
    children = _ref.children,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? "Chip" : _ref$testId,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_CHIP_SIZE : _ref$size,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "regular" : _ref$variant,
    onRemove = _ref.onRemove,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _ref2 = "onClick" in otherProps ? otherProps : _objectSpread(_objectSpread({}, otherProps), {}, {
      onClick: null
    }),
    onClick = _ref2.onClick,
    otherPropsMinusOnClick = _objectWithoutProperties(_ref2, _excluded2);
  var mergedSx = useMemo(function () {
    return merge(baseChipStyles, sx);
  }, [sx]);
  var icon = useGetSubcomponentChild(children, ChipIcon);
  var framedImage = useGetSubcomponentChild(children, ChipFramedImage);
  var avatar = useGetSubcomponentChild(children, ChipAvatar);
  var _useSplitApartChildre = useSplitApartChildrenAndSubComponents(children, [ChipIcon, ChipFramedImage, ChipAvatar]),
    otherChildren = _useSplitApartChildre.otherChildren;
  var leftSlot = useMemo(function () {
    var _ref3, _ref4;
    return (_ref3 = (_ref4 = icon !== null && icon !== void 0 ? icon : framedImage) !== null && _ref4 !== void 0 ? _ref4 : avatar) !== null && _ref3 !== void 0 ? _ref3 : undefined;
  }, [icon, framedImage, avatar]);
  var sizeClass = useGetCurrentSizeClass(size, DEFAULT_CHIP_SIZE, CHIP_SIZES);
  var containsRemoveButton = useMemo(function () {
    return Boolean(onRemove);
  }, [onRemove]);
  var onRemoveClick = useCallback(function (event) {
    event.stopPropagation();
    event.preventDefault();
    onRemove === null || onRemove === void 0 ? void 0 : onRemove(event);
  }, [onRemove]);
  var innerContents = useMemo(function () {
    var _leftSlot$props$size;
    return jsxs(Fragment, {
      children: [leftSlot && jsx(SmartClone, {
        size: (_leftSlot$props$size = leftSlot.props.size) !== null && _leftSlot$props$size !== void 0 ? _leftSlot$props$size : size,
        testId: "".concat(testId, "__leftSlot"),
        children: leftSlot
      }), otherChildren && otherChildren, jsx(Body, {
        size: sizeClass === "large" ? "medium" : "small",
        testId: "".concat(testId, "__label"),
        sx: labelSx,
        className: "Chip__label",
        children: label
      }), jsx(AnimatePresence, {
        children: containsRemoveButton && jsx(ClearFieldButton, {
          onClick: onRemoveClick,
          testId: "".concat(testId, "__removeButton"),
          sx: {
            mx: function mx(_ref5) {
              var base = _ref5.base;
              return "calc(".concat(base.spacing.x1, " * -1)");
            }
          }
        })
      })]
    });
  }, [size, label, leftSlot, testId, sizeClass, containsRemoveButton, onRemoveClick, otherChildren]);
  var baseClickableProps = _objectSpread(_objectSpread(_objectSpread({}, otherPropsMinusOnClick), rc ? {
    rc: rc
  } : {}), {}, {
    onClick: onClick
  });
  var classNames = useMemo(function () {
    return [className, "Chip", {
      "Chip--hasWholeOnClick": Boolean(onClick),
      "Chip--hasRemoveButton": containsRemoveButton
    }, "Chip--".concat(sizeClass, "Size"), "Chip--".concat(variant, "Variant")];
  }, [className, onClick, containsRemoveButton, sizeClass, variant]);
  return jsx(ClassNames, {
    children: function children(_ref6) {
      var cx = _ref6.cx;
      return isBaseClickable(onClick, rc, containsRemoveButton) ? jsx(BaseClickable, _objectSpread(_objectSpread({}, baseClickableProps), {}, {
        testId: testId,
        sx: mergedSx,
        className: cx.apply(void 0, _toConsumableArray(classNames)),
        children: innerContents
      })) : isBox(onClick, rc, containsRemoveButton) ? jsx(Box, _objectSpread(_objectSpread({}, otherPropsMinusOnClick), {}, {
        rc: rc,
        testId: testId,
        sx: mergedSx,
        className: cx.apply(void 0, _toConsumableArray(classNames)),
        children: innerContents
      })) : null;
    }
  });
}
Chip.displayName = "Chip";
Chip.Icon = ChipIcon;
Chip.FramedImage = ChipFramedImage;
Chip.Avatar = ChipAvatar;

export { Chip as C, isBox as a, isBaseClickable as i };
//# sourceMappingURL=Chip.js.map
